import React, { useState, useEffect } from 'react';
import { NetworkConfig } from '../config/networks';
import { trustlineService } from '../services/trustline';
import './PolicyDescription.css';

interface PolicyDescriptionProps {
  currentNetwork?: NetworkConfig;
  contractAddress?: string;
  isConnected?: boolean;
}

const PolicyDescription: React.FC<PolicyDescriptionProps> = ({ currentNetwork, contractAddress, isConnected }) => {
  const [withdrawPolicy, setWithdrawPolicy] = useState<any>(null);
  const [adminWithdrawPolicy, setAdminWithdrawPolicy] = useState<any>(null);
  const [loadingPolicy, setLoadingPolicy] = useState(false);

  // Fetch default policies when network and contract address are available
  useEffect(() => {
    const fetchDefaultPolicies = async () => {
      if (!currentNetwork || !contractAddress) {
        setWithdrawPolicy(null);
        setAdminWithdrawPolicy(null);
        return;
      }

      setLoadingPolicy(true);
      try {
        // Fetch both policies in parallel
        const [withdrawResponse, adminWithdrawResponse] = await Promise.all([
          trustlineService.fetchDefaultPolicyForWithdraw(
            currentNetwork.chainId,
            contractAddress
          ),
          trustlineService.fetchDefaultPolicyForAdminWithdraw(
            currentNetwork.chainId,
            contractAddress
          )
        ]);
        
        if (withdrawResponse && 'result' in withdrawResponse && withdrawResponse.result.success) {
          setWithdrawPolicy(withdrawResponse.result);
        } else {
          setWithdrawPolicy(null);
        }

        if (adminWithdrawResponse && 'result' in adminWithdrawResponse && adminWithdrawResponse.result.success) {
          setAdminWithdrawPolicy(adminWithdrawResponse.result);
        } else {
          setAdminWithdrawPolicy(null);
        }
      } catch (error) {
        console.debug('Failed to fetch default policies:', error);
        setWithdrawPolicy(null);
        setAdminWithdrawPolicy(null);
      } finally {
        setLoadingPolicy(false);
      }
    };

    fetchDefaultPolicies();
  }, [currentNetwork, contractAddress]);

  // Extract email domains from withdraw policy (for Investor)
  const getInvestorEmailDomains = (): string => {
    if (!withdrawPolicy?.policy) {
      return ''; // Default fallback
    }

    const policyData = withdrawPolicy.policy;
    if (policyData.type === 'UserAuthenticationPolicy' && policyData.allowedEmailDomains) {
      return policyData.allowedEmailDomains.join(',');
    }

    return ''; // Default fallback
  };

  // Extract email domains from admin withdraw policy (for Manager)
  const getManagerEmailDomains = (): string => {
    if (!adminWithdrawPolicy?.policy) {
      return ''; // Default fallback
    }

    const policyData = adminWithdrawPolicy.policy;
    if (policyData.type === 'UserAuthenticationPolicy' && policyData.allowedEmailDomains) {
      return policyData.allowedEmailDomains.join(',');
    }

    return ''; // Default fallback
  };

  const investorEmailDomains = getInvestorEmailDomains();
  const managerEmailDomains = getManagerEmailDomains();
  
  // Format emails and domains for display: add @ prefix only to domains, not full emails
  const formatEmailsAndDomains = (emailsAndDomains: string): string => {
    if (!emailsAndDomains) return '';
    return emailsAndDomains
      .split(',')
      .map(item => {
        const trimmed = item.trim();
        // If it's a full email (contains @), don't add @ prefix
        // If it's a domain, add @ prefix
        return trimmed.includes('@') ? trimmed : `@${trimmed}`;
      })
      .join(', ');
  };
  
  return (
    <div className="policy-section">
      <div className="policy-header">
        <h3>Policies</h3>
        {currentNetwork && contractAddress && (
          <a 
            href={`${currentNetwork.blockExplorer}/address/${contractAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="explorer-link"
          >
            View contract on explorer ↗
          </a>
        )}
      </div>
      <div className="policy-content">
        <p className="policy-description">
          Transactions made on{' '}
          {currentNetwork && contractAddress ? (
            <a 
              href={`${currentNetwork.blockExplorer}/address/${contractAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="contract-link"
            >
              this contract
            </a>
          ) : (
            'this contract'
          )}
          {' '}are subject to verifications through Trustline trusted providers.
        </p>
        {isConnected && (
          <div className="policy-groups">
            <div className="policy-group">
              <div className="policy-group-header">
                <span className="role-badge role-badge-investor">Investor</span>
              </div>
              <div className="policy-items">
                <div className="policy-item">
                  <div className="policy-item-header">
                    <span className="policy-name">Watch Lists</span>
                  </div>
                  <ul className="policy-desc policy-list">
                    <li>OFAC Sanctions list</li>
                    <li>Community self-reported lists</li>
                    <li>Service internal lists</li>
                  </ul>
                </div>
                <div className="policy-item">
                  <div className="policy-item-header">
                    <span className="policy-name">End-User Verification</span>
                  </div>
                  <span className="policy-desc">
                  <ul className="policy-desc policy-list">
                    <li>
                      For all transfers, <strong>Email</strong> verification of the beneficiary, which needs to belong to:{' '}
                      {loadingPolicy ? (
                        <strong className="policy-loading">Loading...</strong>
                      ) : investorEmailDomains ? (
                        <strong>{formatEmailsAndDomains(investorEmailDomains)}</strong>
                      ) : (
                        <strong>configured email domains</strong>
                      )}
                    </li>
                    <li>For high amount transfers, <strong>Phone number</strong> verification of beneficary within: <strong>EU or US region</strong>.</li>
                  </ul>
                  </span>
                </div>
                <div className="policy-item">
                  <div className="policy-item-header">
                    <span className="policy-name">Fraud & Hack Prevention</span>
                  </div>
                  <ul className="policy-desc policy-list">
                    <li><strong>Simulate tx impact</strong> on smart contract.</li>
                    <li>Check <strong>risk score</strong> for tx wallets and assets.</li>
                    <li>Require secondary beneficiary verification for <strong>high amount transfers.</strong></li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="policy-group">
              <div className="policy-group-header">
                <span className="role-badge role-badge-manager">Manager</span>
              </div>
              <div className="policy-items">
                <div className="policy-item">
                  <div className="policy-item-header">
                    <span className="policy-name">Watch Lists</span>
                  </div>
                  <ul className="policy-desc policy-list">
                    <li>OFAC Sanctions list</li>
                    <li>Community self-reported lists</li>
                    <li>Service internal lists</li>
                  </ul>
                </div>
                <div className="policy-item">
                  <div className="policy-item-header">
                    <span className="policy-name">End-User Verification</span>
                  </div>
                  <span className="policy-desc">
                  <ul className="policy-desc policy-list">
                    <li>
                      For all transfers, <strong>Email</strong> verification of the beneficiary, which needs to belong to:{' '}
                      {loadingPolicy ? (
                        <strong className="policy-loading">Loading...</strong>
                      ) : managerEmailDomains ? (
                        <strong>{formatEmailsAndDomains(managerEmailDomains)}</strong>
                      ) : (
                        <strong>configured email domains</strong>
                      )}
                    </li>
                    <li>For high amount transfers, <strong>Phone number</strong> verification of beneficary within: <strong>EU or US region</strong>.</li>
                  </ul>
                  </span>
                </div>
                <div className="policy-item">
                  <div className="policy-item-header">
                    <span className="policy-name">Fraud & Hack Prevention</span>
                  </div>
                  <ul className="policy-desc policy-list">
                    <li><strong>Simulate tx impact</strong> on smart contract.</li>
                    <li>Check <strong>risk score</strong> for tx wallets and assets.</li>
                    <li>Require secondary beneficiary verification for <strong>high amount transfers.</strong></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyDescription;
